
import React from 'react';
import { ChangeOrderExportData } from '@/utils/changeOrderExportUtils';
import EstimateHeader from './preview/EstimateHeader';
import EstimateDetails from './preview/EstimateDetails';
import ChangeOrderLineItemsTable from './preview/ChangeOrderLineItemsTable';
import ComprehensiveChangeOrderTable from './preview/ComprehensiveChangeOrderTable';
import EstimateFooter from './preview/EstimateFooter';
import EstimateBankingInfo from './preview/EstimateBankingInfo';
import { useEnhancedUnitResolution } from './preview/hooks/useEnhancedUnitResolution';

interface ChangeOrderPreviewProps {
  data: ChangeOrderExportData;
  className?: string;
  pdfCompatible?: boolean;
}

const ChangeOrderPreview: React.FC<ChangeOrderPreviewProps> = ({ 
  data, 
  className = '',
  pdfCompatible = false 
}) => {
  const { estimate, customer, company, changes, baseVersion, taxAmount, totalAmount, userProfile, documentDefaults } = data;

  // Enhanced unit resolution with custom units
  const { getUnitSymbolWithCustom } = useEnhancedUnitResolution(company?.id);

  // Pass along VAT registration for summary - use fresh company data to ensure accuracy
  const isVatRegistered = company?.is_vat_registered ?? false;
  const companyVatRate = company?.["VAT Rate"] || 15;

  const containerClasses = pdfCompatible 
    ? `bg-white text-black min-h-full p-8 flex flex-col ${className} pdf-compatible-preview`
    : `bg-white text-black min-h-full p-8 flex flex-col ${className}`;

  const fontStyles = pdfCompatible 
    ? { 
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        lineHeight: '1.15'
      }
    : { 
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        lineHeight: '1.15'
      };

  console.log('[ChangeOrderPreview] Rendering with data:', {
    isChangeOrder: data.isChangeOrder,
    hasChanges: !!changes,
    hasBaseVersion: !!baseVersion,
    changesCount: changes ? (changes.added.length + changes.removed.length + changes.modified.length) : 0
  });

  if (!data.isChangeOrder || !changes) {
    console.error('[ChangeOrderPreview] Invalid change order data:', { isChangeOrder: data.isChangeOrder, hasChanges: !!changes });
    return (
      <div className={containerClasses} style={fontStyles}>
        <p className="text-center text-red-600">Error: This is not a valid change order or changes data is missing.</p>
      </div>
    );
  }

  return (
    <div 
      className={containerClasses} 
      id="change-order-preview"
      style={fontStyles}
    >
      <div className="flex-grow">
        <div style={{ fontSize: '11px', lineHeight: '1.15' }}>
          <EstimateHeader company={company} userProfile={userProfile} />
          
          <EstimateDetails estimate={estimate} customer={customer} userProfile={userProfile} company={company} />
        </div>
        
        <ComprehensiveChangeOrderTable 
          changes={changes}
          baseVersionNumber={baseVersion?.version_number || 1}
          changeOrderNumber={estimate.version || 1}
          getUnitSymbol={getUnitSymbolWithCustom}
          taxAmount={taxAmount}
          totalAmount={totalAmount}
          isVatRegistered={isVatRegistered}
          companyVatRate={companyVatRate}
          allLineItems={data.categorizedLineItems?.flatMap(cat => cat.lineItems) || []}
        />

        {/* Banking Information */}
        <EstimateBankingInfo 
          company={company} 
          estimateNumber={estimate.estimate_number} 
        />
      </div>
      
      <div style={{ fontSize: '11px', lineHeight: '1.15' }}>
        <EstimateFooter 
          estimate={estimate} 
          company={company} 
          userProfile={userProfile} 
          customer={customer}
          documentDefaults={documentDefaults}
        />
      </div>
    </div>
  );
};

export default ChangeOrderPreview;
